import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { v4 as uuidv4 } from 'uuid';
import {
  CharonService,
  IHttpOptions,
  IInitializeParam,
  ResultObservable
} from '@quickweb/connect-angular';
import { SessionStorageEnum } from '../../enums/sessionStorage.enum';
import { SdkAnalyticsService } from '../analytics/sdk-analytics.service';




@Injectable({
  providedIn: 'root',
})
export class BaseService {

  private entryPoints: { [key: string]: IInitializeParam } = {};

  private charonService = inject(CharonService);
  private sdkAnalytics = inject(SdkAnalyticsService);



  constructor() {}

  private getConfigSdk() {
    const baseConfig = environment.configDataDog;

    return baseConfig;
  }


  async trackError(options: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'INIT';
    charonID: string;
    relation: string;
    params?: any;
    body?: any;
    error: any;
  }) {
    const { method, charonID, relation, error } = options;

    const trackData = {
      analytics: {
        event_name: 'log',
        parameters: {
          status: 'error',
          message: `Erro na chamada ${method} ${relation} (charonID=${charonID})`,
          context: {
              error: error
          },
        },
      },
    };

    const configSdk = this.getConfigSdk();

    await this.sdkAnalytics
      .track(configSdk, trackData)
      .catch((e: any) =>
        console.error('Falha ao enviar log para DataDog:', e),
      );
  }



  private initializeByCharonID(charonID: string, forceReinit = false): Observable<void> {
    if (this.entryPoints[charonID] && !forceReinit) {
      return of(void 0);
    }

    if (forceReinit) {
      delete this.entryPoints[charonID];
    }

    const entryPointConfig = this.getEntryPointConfig(charonID);

    const params: IInitializeParam = {
      charonID: entryPointConfig.charonid,
      entryPoint: entryPointConfig.entryPoint,
      headers: new HttpHeaders(entryPointConfig.headers),
    };

    this.charonService.setCryptography(entryPointConfig.cryptography);

    return new Observable<void>((observer) => {
      this.charonService.initialize(params).subscribe({
        next: () => {
          this.entryPoints[charonID] = { ...params };
          observer.next();
          observer.complete();
        },
        error: (err) => {
          console.error(`Failed to initialize entryPoint "${charonID}":`, err);


          this.trackError({
            method: 'INIT',
            charonID,
            relation: 'initializeByCharonID',
            params: { charonID },
            body: null,
            error: err,
          });

          observer.error(err);
        },
      });
    });
  }

  private getEntryPointConfig(charonID: string): {
    entryPoint: string;
    cryptography: boolean;
    headers: Record<string, string>;
    charonid: string;
  } {
    const configs: Record<string, {
      entryPoint: string;
      cryptography: boolean;
      charonid: string;
    }> = environment.entryPoints;

    const config = configs[charonID];
    if (!config) {
      throw new Error(`No configuration found for charonID "${charonID}".`);
    }

    const defaultConfig = {
      headers: {
        'Content-Type': 'application/json',
        'x-itau-correlationID': uuidv4(),
        'x-api-key': environment.X_ITAU_API_KEY,
        'x-recaptcha-token': '',
        ...this.getSessionIdHeader()
      }
    };

    return {
      charonid: config.charonid,
      entryPoint: config.entryPoint,
      cryptography: config.cryptography,
      ...defaultConfig,
    };
  }

  private getSessionIdHeader(): Record<string, string> {
    const imobSession = sessionStorage.getItem(SessionStorageEnum.SESSION_ID);
    const authorization = sessionStorage.getItem(SessionStorageEnum.ACCESS_TOKEN);
    return imobSession && authorization ? {
      'x-imob-session': imobSession,
      'authorization': `Bearer ${authorization}`
    } : {};
  }

  private appendSessionHeaders(
    headers: HttpHeaders | Record<string, string> | undefined,
    requiredHeaders: Record<string, string> = {}
  ): HttpHeaders {
    const sessionHeaders = this.getSessionIdHeader();

    let result: HttpHeaders;

    if (headers instanceof HttpHeaders) {
      result = headers;
    } else {
      result = new HttpHeaders(headers || {});
    }

    Object.entries(sessionHeaders).forEach(([k, v]) => {
      result = result.set(k, v);
    });
    Object.entries(requiredHeaders).forEach(([k, v]) => {
      result = result.set(k, v);
    });

    return result;
  }


  private callWithReinitOn403<ResultData, T extends IHttpOptions>(
    requestFn: () => ResultObservable<ResultData, T>,
    charonID: string,
    meta: { method: 'GET' | 'POST' | 'PUT' | 'PATCH'; relation: string; params?: any; body?: any }
  ): ResultObservable<ResultData, T> {
    return requestFn().pipe(
      catchError(err => {

        this.trackError({
          method: meta.method,
          charonID,
          relation: meta.relation,
          params: meta.params,
          body: meta.body,
          error: err,
        });

        if (err?.status === 403) {
          console.warn(`403 recebido para "${charonID}"`);

        }

        return throwError(() => err);
      })
    ) as ResultObservable<ResultData, T>;
  }

  get<ResultData = any, T extends IHttpOptions = {}>(
    relation: string,
    params?: any,
    charonID: string = 'local',
    requiredHeaders: any = null
  ): ResultObservable<ResultData, T> {
    return this.initializeByCharonID(charonID).pipe(
      switchMap(() => {
        const entryPointConfig = this.entryPoints[charonID];
        const mergedHeaders = this.appendSessionHeaders(requiredHeaders || entryPointConfig.headers);

        const defaultOptions: IHttpOptions = {
          charonID: entryPointConfig.charonID,
          entryPoint: entryPointConfig.entryPoint,
          headers: mergedHeaders,
          useHref: false,
          params
        };

        const requestFn = () =>
          this.charonService.get<ResultData, T>(relation, defaultOptions);

        return this.callWithReinitOn403<ResultData, T>(requestFn, charonID, {
          method: 'GET',
          relation,
          params,
        }).pipe(map(response => (response as { body: any }).body));
      })
    );
  }

  post<ResultData = any, T extends IHttpOptions = {}>(
    relation: string,
    params?: any,
    body?: any,
    requiredHeaders: Record<string, string> = {},
    charonID: string = 'local'
  ): ResultObservable<ResultData, T> {
    return this.initializeByCharonID(charonID).pipe(
      switchMap(() => {
        const entryPointConfig = this.entryPoints[charonID];
        const mergedHeaders = this.appendSessionHeaders(entryPointConfig.headers, requiredHeaders);

        const defaultOptions: IHttpOptions = {
          charonID: entryPointConfig.charonID,
          entryPoint: entryPointConfig.entryPoint,
          headers: mergedHeaders,
          useHref: false,
          params,
        };

        const requestFn = () =>
          this.charonService.post<ResultData, T>(relation, body, defaultOptions);

        return this.callWithReinitOn403<ResultData, T>(requestFn, charonID, {
          method: 'POST',
          relation,
          params,
          body,
        }).pipe(map(response => (response as { body: any }).body));
      })
    );
  }

  put<ResultData = any, T extends IHttpOptions = {}>(
    relation: string,
    params?: any,
    body?: any,
    charonID: string = 'local',
    requiredHeaders: any = null
  ): ResultObservable<ResultData, T> {
    return this.initializeByCharonID(charonID).pipe(
      switchMap(() => {
        const entryPointConfig = this.entryPoints[charonID];
        const mergedHeaders = this.appendSessionHeaders(requiredHeaders || entryPointConfig.headers);

        const defaultOptions: IHttpOptions = {
          charonID: entryPointConfig.charonID,
          entryPoint: entryPointConfig.entryPoint,
          headers: mergedHeaders,
          useHref: false,
          params,
        };

        const requestFn = () =>
          this.charonService.put<ResultData, T>(relation, body, defaultOptions);

        return this.callWithReinitOn403<ResultData, T>(requestFn, charonID, {
          method: 'PUT',
          relation,
          params,
          body,
        }).pipe(map(response => (response as { body: any }).body));
      })
    );
  }

  patch<ResultData = any, T extends IHttpOptions = {}>(
    relation: string,
    params?: any,
    body?: any,
    charonID: string = 'local',
    requiredHeaders: any = null
  ): ResultObservable<ResultData, T> {
    return this.initializeByCharonID(charonID).pipe(
      switchMap(() => {
        const entryPointConfig = this.entryPoints[charonID];
        const mergedHeaders = this.appendSessionHeaders(requiredHeaders || entryPointConfig.headers);

        const defaultOptions: IHttpOptions = {
          charonID: entryPointConfig.charonID,
          entryPoint: entryPointConfig.entryPoint,
          headers: mergedHeaders,
          useHref: false,
          params,
        };

        const requestFn = () =>
          this.charonService.patch<ResultData, T>(relation, body, defaultOptions);

        return this.callWithReinitOn403<ResultData, T>(requestFn, charonID, {
          method: 'PATCH',
          relation,
          params,
          body,
        }).pipe(map(response => (response as { body: any }).body));
      })
    );
  }

}
